import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Building2, Folder, File } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

export interface ChainEscapeInfo {
  sharedFolderId: string
  sharedFolderName: string
  recipients: Array<{ tenantId: string; orgName: string }>
  escapingChains: Array<{
    chainId: string
    chainName: string
    targetId: string
    targetName: string
    targetIsFolder: boolean
  }>
}

interface Props {
  escape: ChainEscapeInfo
  /** Option 1: share the chain's target separately + proceed with the move. */
  onShareTarget: () => void | Promise<void>
  /** Option 2: proceed with the move (the link lands greyed for recipients). */
  onMoveAnyway: () => void | Promise<void>
  /** Option 3: abort the move. */
  onCancel: () => void
}

/**
 * Owner-side prompt shown when moving a `.chain` (or a folder containing one)
 * into a cross-tenant shared folder where the chain's target is OUTSIDE the
 * share. The copy is HONEST: the chain itself stays greyed/inert for recipients
 * either way; "Share the source folder too" gives them a SEPARATE share of the
 * target so they can reach it directly. Recipient orgs are listed in their own
 * section (a share can reach several), not named in the sentence.
 */
export function ChainEscapeDialog({ escape, onShareTarget, onMoveAnyway, onCancel }: Props) {
  const { t } = useTranslation('files')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [busy, setBusy] = useState(false)

  const single = escape.escapingChains.length === 1
  const first = escape.escapingChains[0]
  // Several chains can point at the SAME folder — list (and share) each target
  // once, deduped by id. This is exactly what "Share the source folders too" shares.
  const targets = Array.from(new Map(escape.escapingChains.map((c) => [c.targetId, c])).values())

  const run = (fn: () => void | Promise<void>) => async () => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={busy ? undefined : onCancel} />

      <div className={`relative z-10 rounded-lg shadow-xl max-w-xl w-full animate-scale-in ${themeClass} themed-bg-secondary`}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold themed-text mb-2">
                {t(single ? 'driveView.chainEscape.title' : 'driveView.chainEscape.titleMulti')}
              </h3>
              <p className="themed-text-secondary text-sm">
                {single
                  ? t('driveView.chainEscape.body', {
                      chain: first.chainName,
                      shared: escape.sharedFolderName,
                      target: first.targetName,
                    })
                  : t('driveView.chainEscape.bodyMulti', {
                      count: escape.escapingChains.length,
                      shared: escape.sharedFolderName,
                    })}
              </p>
              <p className="themed-text-muted text-xs mt-3">
                {single
                  ? t('driveView.chainEscape.shareNote', { target: first.targetName })
                  : t('driveView.chainEscape.shareNoteMulti')}
              </p>
              {/* The distinct source folders these chains point to (deduped) —
                  exactly what "Share the source folders too" will share. */}
              {!single && (
                <div className="mt-4 px-3 py-2 rounded-md border border-dashed themed-border">
                  <div className="flex items-center gap-1.5 themed-text-muted text-xs mb-1.5">
                    <Folder size={12} className="shrink-0 text-yellow-500" />
                    <span>{t('driveView.chainEscape.targetsLabel')}:</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {targets.map((tg) => (
                      <span key={tg.targetId} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded themed-bg-tertiary themed-text text-xs whitespace-nowrap">
                        {tg.targetIsFolder
                          ? <Folder size={11} className="shrink-0 text-yellow-500" />
                          : <File size={11} className="shrink-0 themed-text-muted" />}
                        <span className="truncate max-w-[12rem]">{tg.targetName}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Who the shared folder reaches — a chain move can touch several
                  orgs, so list them here instead of naming one in the sentence. */}
              <div className="mt-4 px-3 py-2 rounded-md border border-dashed themed-border">
                <div className="flex items-center gap-1.5 themed-text-muted text-xs mb-1.5">
                  <Building2 size={12} className="shrink-0" />
                  <span>{t('driveView.chainEscape.sharedWith')}:</span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {escape.recipients.map((r) => (
                    <span key={r.tenantId} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded themed-bg-tertiary themed-text text-xs whitespace-nowrap">
                      <Building2 size={11} className="shrink-0 themed-text-muted" />
                      <span className="truncate max-w-[12rem]">{r.orgName}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={busy ? undefined : onCancel}
            disabled={busy}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t('driveView.chainEscape.cancel')}
          </button>
          <button
            onClick={run(onMoveAnyway)}
            disabled={busy}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t('driveView.chainEscape.moveAnyway')}
          </button>
          <button
            onClick={run(onShareTarget)}
            disabled={busy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50"
          >
            {t(single ? 'driveView.chainEscape.shareTarget' : 'driveView.chainEscape.shareTargets')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
